import { z } from "zod"

import {
  AspectRatioToImageSizeMap,
  EAspectRatio,
  ELipsyncModel,
  EMossImageGenerationModelType,
  EKreaImageGenerationModelType,
  FluxImageGenerationModelType,
  GoogleAudioModelType,
  GoogleImageGenerationModelType,
  GptImage1Quality,
  ImageQuality,
  ImageSize,
  ImageSizeMap,
  ImageSizeToAspectRatioMap,
  InworldAudioModelType,
  KlingAIVideoGenerationModelType,
  MiniMaxAudioModelType,
  MinimaxImageGenerationModelType,
  NvidiaImageGenerationModelType,
  OpenAIImageGenerationModelType,
  QwenAudioModelType,
  ReplicateVideoGenerationModelType,
  ResembleAIAudioModelType,
  SunoAudioGenerationModelType,
} from "../image/enums"
import type { ImageGenerationModelType, VideoGenerationModelType } from "../image/enums"
import { EModelAvailability } from "../models/configs/availability"
import type { ModelAvailability } from "../models/configs/availability"

export enum ChatGPTModelType {
  // GPT-5.5 gen (newest - April 2026)
  GPT_5_5 = "gpt-5.5",

  // GPT-5.4 gen (March 2026)
  GPT_5_4 = "gpt-5.4",
  GPT_5_4_mini = "gpt-5.4-mini",
  GPT_5_4_NANO = "gpt-5.4-nano",

  // GPT-5.x gen
  GPT_5_2 = "gpt-5.2",
  GPT_5_1 = "gpt-5.1",
  GPT_5 = "gpt-5",
  GPT_5_mini = "gpt-5-mini",
  GPT_5_NANO = "gpt-5-nano",

  // GPT-4.x gen
  GPT_4_1 = "gpt-4.1",
  GPT_4_1_mini = "gpt-4.1-mini",
  GPT_4_1_NANO = "gpt-4.1-nano",
  GPT_4o = "gpt-4o",

  /** @deprecated Use GPT_5_4_NANO or GPT_5_4_mini instead */
  GPT_4o_mini = "gpt-4o-mini",
}

export enum OpenAITranscribeModelType {
  WHISPER_1 = "whisper-1",
  GPT_4O_TRANSCRIBE = "gpt-4o-transcribe",
}

export enum ChatGPTReasoningModelType {
  O1 = "o1",

  O3_MINI = "o3-mini",
}

// Re-export image-related types for backward compatibility
export {
  OpenAIImageGenerationModelType,
  GptImage1Quality,
  FluxImageGenerationModelType,
  GoogleImageGenerationModelType,
  MinimaxImageGenerationModelType,
  NvidiaImageGenerationModelType,
  KlingAIVideoGenerationModelType,
  ReplicateVideoGenerationModelType,
  ImageSize,
  ImageSizeMap,
  EAspectRatio,
  ImageSizeToAspectRatioMap,
  AspectRatioToImageSizeMap,
  ImageQuality,
}
export type { VideoGenerationModelType, ImageGenerationModelType }

export enum ELLMProvider {
  OPENAI = "openai",
  ANTHROPIC = "anthropic",
  OPENROUTER = "openrouter",
  GROQ = "groq",
  GOOGLE = "google",
}

export enum ClaudeModelType {
  // Claude 5.5 (newest)
  SONNET_5_5 = "claude-sonnet-5-5",
  HAIKU_5_5 = "claude-haiku-5-5",
  // Claude 5
  SONNET_5 = "claude-sonnet-5",
  // Claude 4.8 (newest)
  OPUS_48 = "claude-opus-4-8",
  // Claude 4.6
  OPUS_46 = "claude-opus-4-6",
  SONNET_46 = "claude-sonnet-4-6",
  // Claude 4.5
  OPUS_45 = "claude-opus-4-5-20251101",
  SONNET_45 = "claude-sonnet-4-5-20250929",
  // Claude 4.1
  OPUS_41 = "claude-opus-4-1-20250805",
  // Claude 4
  // Claude 4.5 Haiku
  HAIKU_45 = "claude-haiku-4-5-20251001",
  // Claude 3.x (deprecated/retired)
  /** @deprecated Retired Jan 2026 */
  /** @deprecated Retired Jan 2026 */
  /** @deprecated Use OPUS_4 or later */
  /** @deprecated Use HAIKU_45 instead — retiring Apr 2026 */
  /** @deprecated */
}

export enum ElevenLabsModelType {
  MONOLINGUAL_v1 = "eleven_monolingual_v1",
  MULTILINGUAL_v2 = "eleven_multilingual_v2",
  FLASH_v2_5 = "eleven_flash_v2_5",
  // Eleven v3 — newest model. Supports audio tags ([whispers], [laughs], etc.),
  // 70+ languages, and richer emotional control. See:
  // https://elevenlabs.io/docs/overview/models#eleven-v3
  V3 = "eleven_v3",
}

export enum AwsPollyModelType {
  AWS_POLLY_STANDARD = "aws_polly_standard",
  AWS_POLLY_NEURAL = "aws_polly_neural",
  AWS_POLLY_GENERATIVE = "aws_polly_generative",
  AWS_POLLY_LONGFORM = "aws_polly_longform",
}

export type AudioGenerationModelType = ElevenLabsModelType | AwsPollyModelType

export enum OpenRouterModelType {
  // Z.AI GLM
  GLM_5_3 = "z-ai/glm-5.3", // https://openrouter.ai/z-ai/glm-5.3
  GLM_5_1 = "z-ai/glm-5.1", // https://openrouter.ai/z-ai/glm-5.1
  // MiniMax (newest)
  MINIMAX_M2_7 = "minimax/minimax-m2.7", // https://openrouter.ai/minimax/minimax-m2.7
  // Qwen 3.5 (newest)
  QWEN_3_5_397B_A17B = "qwen/qwen3.5-397b-a17b", // https://openrouter.ai/qwen/qwen3.5-397b-a17b — flagship agent model
  QWEN_3_5_122B_A10B = "qwen/qwen3.5-122b-a10b", // https://openrouter.ai/qwen/qwen3.5-122b-a10b — best balance
  // Qwen 3
  QWEN_3_CODER_NEXT = "qwen/qwen3-coder-next", // https://openrouter.ai/qwen/qwen3-coder-next — coding agent specialist
  QWEN_3_235B_A22B = "qwen/qwen3-235b-a22b", // https://openrouter.ai/qwen/qwen3-235b-a22b (MoE, 22B active)
  QWEN_3_32B = "qwen/qwen3-32b", // https://openrouter.ai/qwen/qwen3-32b
  QWEN_3_8_27B = "qwen/qwen3.8-27b", // https://openrouter.ai/qwen/qwen3.8-27b — dense, Apache 2.0
  // OpenAI via OpenRouter (not on the native OpenAI path yet)
  GPT_6_LUNA = "openai/gpt-6-luna", // https://openrouter.ai/openai/gpt-6-luna
  // xAI (via OpenRouter — no direct x-ai integration yet)
  GROK_4_3 = "x-ai/grok-4.3", // https://openrouter.ai/x-ai/grok-4.3
  // DeepSeek
  DEEPSEEK_V4_PRO = "deepseek/deepseek-v4-pro", // https://openrouter.ai/deepseek/deepseek-v4-pro
  DEEPSEEK_V4_FLASH = "deepseek/deepseek-v4-flash", // https://openrouter.ai/deepseek/deepseek-v4-flash
  // Dated snapshots: the versions Artificial Analysis scored on its agentic index
  DEEPSEEK_V4_PRO_0813 = "deepseek/deepseek-v4-pro-0813", // https://openrouter.ai/deepseek/deepseek-v4-pro-0813
  DEEPSEEK_V4_FLASH_0731 = "deepseek/deepseek-v4-flash-0731", // https://openrouter.ai/deepseek/deepseek-v4-flash-0731
  DEEPSEEK_R1 = "deepseek/deepseek-r1", // https://openrouter.ai/deepseek/deepseek-r1
  DEEPSEEK_R1_DISTILL_LLAMA_70B = "deepseek/deepseek-r1-distill-llama-70b", // https://openrouter.ai/deepseek/deepseek-r1-distill-llama-70b
  DEEPSEEK_CHAT_V3 = "deepseek/deepseek-chat", // https://openrouter.ai/deepseek/deepseek-chat
  // Google via OpenRouter
  // Qwen (older)
  QWEN_2_5_CODER_32B_INSTRUCT = "qwen/qwen-2.5-coder-32b-instruct", // https://openrouter.ai/qwen/qwen-2.5-coder-32b-instruct
}

export enum GroqModelType {
  LLAMA_3_3_70B_VERSATILE = "llama-3.3-70b-versatile",
}

export enum GeminiModelType {
  // Gemini 3.8 Flash (newest fast model; default vision model)
  GEMINI_3_8_FLASH = "gemini-3.8-flash",
  // Gemini 3.5 Flash (Apr 2026)
  GEMINI_3_5_FLASH = "gemini-3.5-flash",
  // Gemini 3.1 (Feb 2026)
  GEMINI_3_1_PRO_PREVIEW = "gemini-3.1-pro-preview",
  // Gemini 3 (deprecated March 9, 2026)
  // Gemini 3 Flash (standard 3.x flash; latest fast model)
  GEMINI_3_FLASH_PREVIEW = "gemini-3-flash-preview",
  // Gemini 3.1 Flash Lite (cheapest vision model)
  GEMINI_3_1_FLASH_LITE = "gemini-3.1-flash-lite",
  // Gemini 3.1 Flash Image (vision + image gen)
  GEMINI_3_1_FLASH_IMAGE_PREVIEW = "gemini-3.1-flash-image-preview",
  // Gemini 2.5
  GEMINI_2_5_FLASH = "gemini-2.5-flash",
  GEMINI_2_5_FLASH_LITE = "gemini-2.5-flash-lite",
}

// Order that you want the providers to be in. Very important to specify routers for certain models
export const OPENROUTER_ORDER_MAP = {
  [OpenRouterModelType.DEEPSEEK_CHAT_V3]: ["DeepSeek", "Fireworks"],
  [OpenRouterModelType.DEEPSEEK_R1]: ["DeepSeek", "Fireworks", "Together"],
  [OpenRouterModelType.DEEPSEEK_R1_DISTILL_LLAMA_70B]: ["DeepInfra", "NovitaAI"],
}

export type IAllModelTypes =
  | ClaudeModelType
  | ChatGPTModelType
  | OpenRouterModelType
  | GroqModelType
  | GeminiModelType
  | ChatGPTReasoningModelType
  | ElevenLabsModelType
  | AwsPollyModelType
  | MiniMaxAudioModelType
  | ResembleAIAudioModelType
  | QwenAudioModelType
  | InworldAudioModelType
  | GoogleAudioModelType
  | SunoAudioGenerationModelType
  | ELipsyncModel
  | EMossImageGenerationModelType
  | EKreaImageGenerationModelType
  | OpenAIImageGenerationModelType
  | FluxImageGenerationModelType
  | GoogleImageGenerationModelType
  | MinimaxImageGenerationModelType
  | NvidiaImageGenerationModelType
  | KlingAIVideoGenerationModelType
  | ReplicateVideoGenerationModelType

export const zAllModelTypes = z.union([
  z.nativeEnum(ChatGPTModelType),
  z.nativeEnum(ClaudeModelType),
  z.nativeEnum(OpenRouterModelType),
  z.nativeEnum(GroqModelType),
  z.nativeEnum(GeminiModelType),
  z.nativeEnum(ChatGPTReasoningModelType),
  z.nativeEnum(ElevenLabsModelType),
  z.nativeEnum(AwsPollyModelType),
  z.nativeEnum(EMossImageGenerationModelType),
  z.nativeEnum(EKreaImageGenerationModelType),
  z.nativeEnum(OpenAIImageGenerationModelType),
  z.nativeEnum(FluxImageGenerationModelType),
  z.nativeEnum(GoogleImageGenerationModelType),
  z.nativeEnum(MinimaxImageGenerationModelType),
  z.nativeEnum(NvidiaImageGenerationModelType),
  z.nativeEnum(KlingAIVideoGenerationModelType),
  z.nativeEnum(ReplicateVideoGenerationModelType),
])

/** Model types compatible with AI SDK providers (Anthropic, OpenAI, Google) */
export type AISDKModelType = ChatGPTModelType | ChatGPTReasoningModelType | ClaudeModelType | GeminiModelType

/**
 * Detect the AI SDK provider for a given model enum value.
 * Returns "anthropic", "openai", or "google".
 * Defaults to "anthropic" for unrecognized prefixes.
 */
export const resolveModelProvider = (
  modelId: AISDKModelType,
): ELLMProvider.ANTHROPIC | ELLMProvider.OPENAI | ELLMProvider.GOOGLE => {
  const id = modelId as string
  if (id.startsWith("gpt-") || id.startsWith("o1") || id.startsWith("o3") || id.startsWith("o4")) {
    return ELLMProvider.OPENAI
  }
  if (id.startsWith("gemini-")) {
    return ELLMProvider.GOOGLE
  }
  return ELLMProvider.ANTHROPIC
}

export const LLMProviderDisplayNames = {
  [ELLMProvider.OPENAI]: "OpenAI",
  [ELLMProvider.ANTHROPIC]: "Anthropic",
  [ELLMProvider.OPENROUTER]: "OpenRouter",
  [ELLMProvider.GROQ]: "Groq",
  [ELLMProvider.GOOGLE]: "Google",
} as const

/**
 * Global metadata for LLM models - sortOrder (lower = newer/better), tier, provider
 */
export interface LLMModelMeta {
  sortOrder: number
  tier: "flagship" | "standard" | "budget"
  provider: ELLMProvider
  /** Anthropic thinking-off wire value; omitted models keep "disabled". */
  anthropicThinkingOffType?: "disabled" | "between_tools"
  /**
   * Code-owned availability (VM-966). JSDoc `@deprecated` isn't runtime-readable and the
   * admin registry is forbidden from inferring availability, so deprecated/retired LLMs are
   * annotated explicitly here. Omit to default to ENABLED via `resolveAvailability`.
   */
  availability?: ModelAvailability
}

/** Reusable LEGACY annotation for retired/superseded LLM entries (VM-966). */
const legacyLLM = (reason: string): ModelAvailability => ({ status: EModelAvailability.LEGACY, reason })

const LLM_MODEL_METADATA_RAW = {
  // OpenAI GPT-5.4 gen (newest - March 2026)
  [ChatGPTModelType.GPT_5_5]: { sortOrder: -3, tier: "flagship", provider: ELLMProvider.OPENAI },

  [ChatGPTModelType.GPT_5_4]: { sortOrder: -2, tier: "flagship", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_5_4_mini]: { sortOrder: -1, tier: "standard", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_5_4_NANO]: { sortOrder: 0, tier: "budget", provider: ELLMProvider.OPENAI },

  // OpenAI GPT-5.x gen
  [ChatGPTModelType.GPT_5_2]: { sortOrder: 1, tier: "flagship", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_5_1]: { sortOrder: 2, tier: "flagship", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_5]: { sortOrder: 3, tier: "flagship", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_5_mini]: { sortOrder: 4, tier: "standard", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_5_NANO]: { sortOrder: 5, tier: "budget", provider: ELLMProvider.OPENAI },

  // OpenAI GPT-4.1 gen
  [ChatGPTModelType.GPT_4_1]: { sortOrder: 10, tier: "flagship", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_4_1_mini]: { sortOrder: 11, tier: "standard", provider: ELLMProvider.OPENAI },
  [ChatGPTModelType.GPT_4_1_NANO]: { sortOrder: 12, tier: "budget", provider: ELLMProvider.OPENAI },

  // OpenAI GPT-4.5/4o gen (legacy)
  [ChatGPTModelType.GPT_4o]: { sortOrder: 21, tier: "standard", provider: ELLMProvider.OPENAI },
  /** @deprecated Use GPT_5_4_NANO instead */
  [ChatGPTModelType.GPT_4o_mini]: {
    sortOrder: 22,
    tier: "budget",
    provider: ELLMProvider.OPENAI,
    availability: legacyLLM("Deprecated — use GPT-5.4 Nano/Mini"),
  },

  // OpenAI Reasoning
  [ChatGPTReasoningModelType.O3_MINI]: { sortOrder: 30, tier: "flagship", provider: ELLMProvider.OPENAI },
  [ChatGPTReasoningModelType.O1]: { sortOrder: 31, tier: "flagship", provider: ELLMProvider.OPENAI },

  // Claude 5.5 (newest)
  [ClaudeModelType.SONNET_5_5]: {
    sortOrder: 35.5,
    tier: "standard",
    provider: ELLMProvider.ANTHROPIC,
    anthropicThinkingOffType: "between_tools",
  },
  [ClaudeModelType.HAIKU_5_5]: { sortOrder: 35.75, tier: "budget", provider: ELLMProvider.ANTHROPIC },
  // Claude 5
  [ClaudeModelType.SONNET_5]: { sortOrder: 36, tier: "standard", provider: ELLMProvider.ANTHROPIC },
  // Claude 4.8
  [ClaudeModelType.OPUS_48]: { sortOrder: 37, tier: "flagship", provider: ELLMProvider.ANTHROPIC },
  // Claude 4.6
  [ClaudeModelType.SONNET_46]: { sortOrder: 38, tier: "standard", provider: ELLMProvider.ANTHROPIC },
  // Claude 4.5
  [ClaudeModelType.OPUS_46]: { sortOrder: 39, tier: "flagship", provider: ELLMProvider.ANTHROPIC },
  [ClaudeModelType.OPUS_45]: { sortOrder: 40, tier: "flagship", provider: ELLMProvider.ANTHROPIC },
  [ClaudeModelType.SONNET_45]: { sortOrder: 41, tier: "flagship", provider: ELLMProvider.ANTHROPIC },
  [ClaudeModelType.HAIKU_45]: { sortOrder: 41.5, tier: "budget", provider: ELLMProvider.ANTHROPIC },
  // Claude 3.x (deprecated / retired — annotated LEGACY for the code-owned registry)

  // Gemini (newest first)
  [GeminiModelType.GEMINI_3_8_FLASH]: { sortOrder: 45.5, tier: "standard", provider: ELLMProvider.GOOGLE },
  [GeminiModelType.GEMINI_3_5_FLASH]: { sortOrder: 46, tier: "standard", provider: ELLMProvider.GOOGLE },
  [GeminiModelType.GEMINI_3_1_PRO_PREVIEW]: { sortOrder: 49, tier: "flagship", provider: ELLMProvider.GOOGLE },
  [GeminiModelType.GEMINI_3_FLASH_PREVIEW]: { sortOrder: 47.5, tier: "standard", provider: ELLMProvider.GOOGLE },
  [GeminiModelType.GEMINI_3_1_FLASH_LITE]: { sortOrder: 48, tier: "budget", provider: ELLMProvider.GOOGLE },
  [GeminiModelType.GEMINI_3_1_FLASH_IMAGE_PREVIEW]: {
    sortOrder: 48.5,
    tier: "standard",
    provider: ELLMProvider.GOOGLE,
  },
  [GeminiModelType.GEMINI_2_5_FLASH]: { sortOrder: 52, tier: "standard", provider: ELLMProvider.GOOGLE },
  [GeminiModelType.GEMINI_2_5_FLASH_LITE]: { sortOrder: 53, tier: "budget", provider: ELLMProvider.GOOGLE },

  // OpenRouter
  [OpenRouterModelType.GLM_5_3]: { sortOrder: 53, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.GLM_5_1]: { sortOrder: 54, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.MINIMAX_M2_7]: { sortOrder: 55, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.QWEN_3_5_397B_A17B]: { sortOrder: 56, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.QWEN_3_5_122B_A10B]: { sortOrder: 57, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.QWEN_3_CODER_NEXT]: { sortOrder: 58, tier: "standard", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.QWEN_3_235B_A22B]: { sortOrder: 59, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.QWEN_3_32B]: { sortOrder: 60, tier: "standard", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.QWEN_3_8_27B]: { sortOrder: 55.5, tier: "standard", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.GROK_4_3]: { sortOrder: 59, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.GPT_6_LUNA]: { sortOrder: 59, tier: "standard", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_V4_PRO]: { sortOrder: 60, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_V4_FLASH]: { sortOrder: 61, tier: "standard", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_V4_PRO_0813]: { sortOrder: 59.5, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_V4_FLASH_0731]: { sortOrder: 60.5, tier: "budget", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_R1]: { sortOrder: 61, tier: "flagship", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_CHAT_V3]: { sortOrder: 62, tier: "standard", provider: ELLMProvider.OPENROUTER },
  [OpenRouterModelType.DEEPSEEK_R1_DISTILL_LLAMA_70B]: {
    sortOrder: 63,
    tier: "standard",
    provider: ELLMProvider.OPENROUTER,
  },
  [OpenRouterModelType.QWEN_2_5_CODER_32B_INSTRUCT]: {
    sortOrder: 63,
    tier: "standard",
    provider: ELLMProvider.OPENROUTER,
  },

  // Groq
  [GroqModelType.LLAMA_3_3_70B_VERSATILE]: { sortOrder: 70, tier: "standard", provider: ELLMProvider.GROQ },
} as const satisfies Partial<Record<IAllModelTypes, LLMModelMeta>>

export type ListedLLMModelType = keyof typeof LLM_MODEL_METADATA_RAW

export const LLM_MODEL_METADATA: Record<string, LLMModelMeta | undefined> & typeof LLM_MODEL_METADATA_RAW =
  LLM_MODEL_METADATA_RAW

const _getModelsByProvider = (provider: ELLMProvider): string[] =>
  Object.entries(LLM_MODEL_METADATA_RAW)
    .filter(([_, meta]) => meta.provider === provider)
    .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
    .map(([model]) => model)

/**
 * Display names for LLM models - human readable names
 */
export const LLM_MODEL_DISPLAY_NAMES: Record<string, string> = {
  // OpenAI GPT-5.4 gen (newest)
  [ChatGPTModelType.GPT_5_5]: "GPT-5.5",
  [ChatGPTModelType.GPT_5_4]: "GPT-5.4",
  [ChatGPTModelType.GPT_5_4_mini]: "GPT-5.4 Mini",
  [ChatGPTModelType.GPT_5_4_NANO]: "GPT-5.4 Nano",
  // OpenAI GPT-5.x gen
  [ChatGPTModelType.GPT_5_2]: "GPT-5.2",
  [ChatGPTModelType.GPT_5_1]: "GPT-5.1",
  [ChatGPTModelType.GPT_5]: "GPT-5",
  [ChatGPTModelType.GPT_5_mini]: "GPT-5 Mini",
  [ChatGPTModelType.GPT_5_NANO]: "GPT-5 Nano",
  // OpenAI GPT-4.1 gen
  [ChatGPTModelType.GPT_4_1]: "GPT-4.1",
  [ChatGPTModelType.GPT_4_1_mini]: "GPT-4.1 Mini",
  [ChatGPTModelType.GPT_4_1_NANO]: "GPT-4.1 Nano",
  // OpenAI GPT-4.5/4o gen (legacy)
  [ChatGPTModelType.GPT_4o]: "GPT-4o",
  /** @deprecated */ [ChatGPTModelType.GPT_4o_mini]: "GPT-4o Mini",
  // OpenAI Reasoning
  [ChatGPTReasoningModelType.O3_MINI]: "O3 Mini",
  [ChatGPTReasoningModelType.O1]: "O1",
  // Claude
  [ClaudeModelType.SONNET_5_5]: "Claude 5.5 Sonnet",
  [ClaudeModelType.HAIKU_5_5]: "Claude 5.5 Haiku",
  [ClaudeModelType.SONNET_5]: "Claude 5 Sonnet",
  [ClaudeModelType.OPUS_48]: "Claude 4.8 Opus",
  [ClaudeModelType.SONNET_46]: "Claude 4.6 Sonnet",
  [ClaudeModelType.OPUS_46]: "Claude 4.6 Opus",
  [ClaudeModelType.OPUS_45]: "Claude 4.5 Opus",
  [ClaudeModelType.SONNET_45]: "Claude 4.5 Sonnet",
  [ClaudeModelType.HAIKU_45]: "Claude 4.5 Haiku",
  // Gemini
  [GeminiModelType.GEMINI_3_8_FLASH]: "Gemini 3.8 Flash",
  [GeminiModelType.GEMINI_3_5_FLASH]: "Gemini 3.5 Flash",
  [GeminiModelType.GEMINI_3_1_PRO_PREVIEW]: "Gemini 3.1 Pro",
  [GeminiModelType.GEMINI_3_FLASH_PREVIEW]: "Gemini 3 Flash",
  [GeminiModelType.GEMINI_3_1_FLASH_LITE]: "Gemini 3.1 Flash Lite",
  [GeminiModelType.GEMINI_3_1_FLASH_IMAGE_PREVIEW]: "Gemini 3.1 Flash Image",
  [GeminiModelType.GEMINI_2_5_FLASH]: "Gemini 2.5 Flash",
  [GeminiModelType.GEMINI_2_5_FLASH_LITE]: "Gemini 2.5 Flash Lite",
  // OpenRouter
  [OpenRouterModelType.GLM_5_3]: "GLM 5.3",
  [OpenRouterModelType.GLM_5_1]: "GLM 5.1",
  [OpenRouterModelType.MINIMAX_M2_7]: "MiniMax M2.7",
  [OpenRouterModelType.QWEN_3_5_397B_A17B]: "Qwen 3.5 397B",
  [OpenRouterModelType.QWEN_3_5_122B_A10B]: "Qwen 3.5 122B",
  [OpenRouterModelType.QWEN_3_CODER_NEXT]: "Qwen 3 Coder Next",
  [OpenRouterModelType.QWEN_3_235B_A22B]: "Qwen 3 235B",
  [OpenRouterModelType.QWEN_3_32B]: "Qwen 3 32B",
  [OpenRouterModelType.QWEN_3_8_27B]: "Qwen 3.8 27B",
  [OpenRouterModelType.GROK_4_3]: "Grok 4.3",
  [OpenRouterModelType.GPT_6_LUNA]: "GPT-6 Luna",
  [OpenRouterModelType.DEEPSEEK_V4_PRO]: "DeepSeek V4 Pro",
  [OpenRouterModelType.DEEPSEEK_V4_FLASH]: "DeepSeek V4 Flash",
  [OpenRouterModelType.DEEPSEEK_V4_PRO_0813]: "DeepSeek V4 Pro (0813)",
  [OpenRouterModelType.DEEPSEEK_V4_FLASH_0731]: "DeepSeek V4 Flash (0731)",
  [OpenRouterModelType.DEEPSEEK_R1]: "DeepSeek R1",
  [OpenRouterModelType.DEEPSEEK_CHAT_V3]: "DeepSeek V3",
  [OpenRouterModelType.DEEPSEEK_R1_DISTILL_LLAMA_70B]: "DeepSeek R1 Distill 70B",
  [OpenRouterModelType.QWEN_2_5_CODER_32B_INSTRUCT]: "Qwen 2.5 Coder 32B",
  // Groq
  [GroqModelType.LLAMA_3_3_70B_VERSATILE]: "Llama 3.3 70B",
}

/**
 * Model info with full details
 */
export interface ModelInfo {
  id: string
  name: string
  provider: ELLMProvider
  providerName: string
  tier: "flagship" | "standard" | "budget"
  sortOrder: number
}

/**
 * Get model info by ID
 */
export const getModelInfo = (modelId: string): ModelInfo | null => {
  const meta = LLM_MODEL_METADATA[modelId]
  if (!meta) return null
  return {
    id: modelId,
    name: LLM_MODEL_DISPLAY_NAMES[modelId] || modelId,
    provider: meta.provider,
    providerName: LLMProviderDisplayNames[meta.provider],
    tier: meta.tier,
    sortOrder: meta.sortOrder,
  }
}

/**
 * Get sorted/filtered LLM models
 * @param options.models - Array of models to sort (returns all if omitted)
 * @param options.provider - Filter by provider (openai, anthropic, google, etc.)
 * @param options.tier - Filter by tier (flagship, fast, budget)
 * @param options.latest - If true, returns only the latest model per provider
 */
export const getSortedModels = (options?: {
  models?: string[]
  provider?: ELLMProvider
  tier?: "flagship" | "standard" | "budget"
  latest?: boolean
}): string[] => {
  interface ModelMeta {
    sortOrder: number
    tier: string
    provider: ELLMProvider
  }
  let result: [string, ModelMeta][]

  if (options?.models) {
    // Sort provided models
    result = options.models.flatMap((model) => {
      const meta = LLM_MODEL_METADATA[model]
      return meta ? [[model, meta]] : []
    })
  } else {
    result = Object.entries(LLM_MODEL_METADATA_RAW)
  }

  // Filter by provider
  if (options?.provider) {
    result = result.filter(([_, meta]) => meta.provider === options.provider)
  }

  // Filter by tier
  if (options?.tier) {
    result = result.filter(([_, meta]) => meta.tier === options.tier)
  }

  // Sort by sortOrder
  result.sort((a, b) => a[1].sortOrder - b[1].sortOrder)

  // If latest, get first per provider
  if (options?.latest) {
    const seen = new Set<ELLMProvider>()
    result = result.filter(([_, meta]) => {
      if (seen.has(meta.provider)) return false
      seen.add(meta.provider)
      return true
    })
  }

  return result.map(([model]) => model)
}

/**
 * Get models with full info, optionally grouped by provider family
 * @param options.models - Array of models to include (returns all if omitted)
 * @param options.provider - Filter by provider
 * @param options.tier - Filter by tier
 * @param options.groupByFamily - If true, returns models grouped by provider
 */
export const getSortedModelsWithInfo = (options?: {
  models?: string[]
  provider?: ELLMProvider
  tier?: "flagship" | "standard" | "budget"
  groupByFamily?: boolean
}): { sorted: ModelInfo[]; byFamily: Record<ELLMProvider, ModelInfo[]> } => {
  const modelIds = getSortedModels({
    models: options?.models,
    provider: options?.provider,
    tier: options?.tier,
  })

  const sorted = modelIds.map((id) => getModelInfo(id)!).filter(Boolean)

  // Group by provider family
  const byFamily: Record<ELLMProvider, ModelInfo[]> = {
    [ELLMProvider.OPENAI]: [],
    [ELLMProvider.ANTHROPIC]: [],
    [ELLMProvider.GOOGLE]: [],
    [ELLMProvider.OPENROUTER]: [],
    [ELLMProvider.GROQ]: [],
  }

  sorted.forEach((model) => {
    byFamily[model.provider].push(model)
  })

  // Remove empty providers
  Object.keys(byFamily).forEach((key) => {
    if (byFamily[key as ELLMProvider].length === 0) {
      delete byFamily[key as ELLMProvider]
    }
  })

  return { sorted, byFamily }
}

// ============================================================================
// CURATED MODEL LISTS
// ============================================================================

/** Providers included in curated model lists (the big 3) */
const CURATED_PROVIDERS = [ELLMProvider.OPENAI, ELLMProvider.ANTHROPIC, ELLMProvider.GOOGLE]

/**
 * Get curated LLM models — the best models across the main providers, grouped by tier.
 * Returns the latest model per provider per tier (e.g., 3 flagship, 3 fast, 3 budget).
 *
 * @param options.providers - Override which providers to include (default: OpenAI, Anthropic, Google)
 * @param options.tiers - Override which tiers to include (default: all 3)
 * @param options.maxPerTier - Max models per tier (default: unlimited — one per provider)
 */
export const getCuratedLLMModels = (options?: {
  providers?: ELLMProvider[]
  tiers?: Array<"flagship" | "standard" | "budget">
  maxPerTier?: number
}): ModelInfo[] => {
  const providers = options?.providers ?? CURATED_PROVIDERS
  const tiers = options?.tiers ?? ["flagship", "standard", "budget"]

  const result: ModelInfo[] = []

  for (const tier of tiers) {
    // Get latest model per provider for this tier
    const tierModels = getSortedModels({ tier })
      .map((id) => getModelInfo(id)!)
      .filter((m) => m && providers.includes(m.provider))

    // Dedupe: one per provider, sorted by sortOrder (best first)
    const seen = new Set<ELLMProvider>()
    const deduped = tierModels.filter((m) => {
      if (seen.has(m.provider)) return false
      seen.add(m.provider)
      return true
    })

    const limited = options?.maxPerTier ? deduped.slice(0, options.maxPerTier) : deduped
    result.push(...limited)
  }

  return result
}

/**
 * Get curated LLM model IDs — convenience wrapper returning just IDs.
 * Same options as getCuratedLLMModels.
 */
export const getCuratedLLMModelIds = (options?: Parameters<typeof getCuratedLLMModels>[0]): string[] => {
  return getCuratedLLMModels(options).map((m) => m.id)
}

// ============================================================================
// AGENT-OPTIMIZED MODELS
// ============================================================================

/** Models specifically suited for agentic tasks (tool calling, coding, multi-step reasoning) */
const AGENT_OPTIMIZED_MODELS: Array<{
  id: string
  category: "coding" | "general" | "reasoning" | "budget"
  description: string
}> = [
  // Coding agents
  {
    id: OpenRouterModelType.QWEN_3_CODER_NEXT,
    category: "coding",
    description: "Purpose-built coding agent, 80B MoE/3B active, 256K ctx",
  },
  {
    id: ChatGPTModelType.GPT_5_4,
    category: "coding",
    description: "Latest GPT, strong tool calling and code generation",
  },

  // General agentic
  {
    id: OpenRouterModelType.QWEN_3_5_397B_A17B,
    category: "general",
    description: "Qwen flagship, top agent benchmarks, 262K ctx",
  },
  {
    id: OpenRouterModelType.QWEN_3_5_122B_A10B,
    category: "general",
    description: "Best balance of cost and agent performance",
  },
  { id: ClaudeModelType.OPUS_46, category: "general", description: "Most capable Claude, deep reasoning" },
  { id: OpenRouterModelType.MINIMAX_M2_7, category: "general", description: "MiniMax flagship model" },
  {
    id: OpenRouterModelType.DEEPSEEK_V4_PRO,
    category: "general",
    description: "Top open-weight agent model (MIT), 1M ctx",
  },

  // Reasoning-heavy
  { id: OpenRouterModelType.DEEPSEEK_R1, category: "reasoning", description: "Chain-of-thought reasoning specialist" },
  {
    id: ChatGPTReasoningModelType.O3_MINI,
    category: "reasoning",
    description: "OpenAI reasoning model, good for planning",
  },

  // Budget-friendly agents
  { id: ChatGPTModelType.GPT_5_4_mini, category: "budget", description: "Fast and cheap, solid tool calling" },
  { id: OpenRouterModelType.GPT_6_LUNA, category: "budget", description: "Cheap, fast GPT-6 tier, 1M ctx" },
  { id: GeminiModelType.GEMINI_2_5_FLASH, category: "budget", description: "Fast with adaptive reasoning, 1M ctx" },
  {
    id: OpenRouterModelType.QWEN_3_32B,
    category: "budget",
    description: "Cheap and capable, good for high-volume agent loops",
  },
  { id: OpenRouterModelType.DEEPSEEK_CHAT_V3, category: "budget", description: "Strong general model, very low cost" },
]

export interface AgentModelInfo extends ModelInfo {
  category: "coding" | "general" | "reasoning" | "budget"
  description: string
}

/**
 * Get agent-optimized models for frontend display.
 * Returns models specifically suited for agentic tasks (tool calling, coding, multi-step reasoning).
 *
 * @param options.category - Filter by category: "coding", "general", "reasoning", "budget"
 */
export const getAgentOptimizedModels = (options?: {
  category?: "coding" | "general" | "reasoning" | "budget"
}): AgentModelInfo[] => {
  let models = AGENT_OPTIMIZED_MODELS

  if (options?.category) {
    models = models.filter((m) => m.category === options.category)
  }

  return models
    .map((m) => {
      const info = getModelInfo(m.id)
      if (!info) return null
      return { ...info, category: m.category, description: m.description }
    })
    .filter(Boolean) as AgentModelInfo[]
}

/**
 * Get agent-optimized model IDs — convenience wrapper.
 */
export const getAgentOptimizedModelIds = (options?: Parameters<typeof getAgentOptimizedModels>[0]): string[] => {
  return getAgentOptimizedModels(options).map((m) => m.id)
}

/**
 * Mastra playground model shortlist — top agentic picks by AA Agentic Index (Sep 2026) and price.
 * Doubles as the server allowlist for per-request model overrides (vidmax-api service/mastraOverrides.ts).
 */
const MASTRA_OVERRIDE_MODELS: Array<{ id: string; description: string }> = [
  { id: OpenRouterModelType.GLM_5_3, description: "Top open-weight agent model (MIT), ~$0.22 / $3.39 per 1M" },
  {
    id: OpenRouterModelType.DEEPSEEK_V4_PRO_0813,
    description: "Strong agent at mid price (MIT), ~$0.66 / $1.98 per 1M",
  },
  { id: OpenRouterModelType.QWEN_3_8_27B, description: "Small dense agent model (Apache 2.0), ~$0.42 / $3.00 per 1M" },
  { id: OpenRouterModelType.DEEPSEEK_V4_FLASH_0731, description: "Best agentic value (MIT), ~$0.01 / $1.28 per 1M" },
  { id: OpenRouterModelType.GPT_6_LUNA, description: "Cheap, fast GPT-6 tier (closed), $0.10 / $0.50 per 1M" },
]

export const getMastraOverrideModels = (): Array<ModelInfo & { description: string }> =>
  MASTRA_OVERRIDE_MODELS.flatMap((m) => {
    const info = getModelInfo(m.id)
    return info ? [{ ...info, description: m.description }] : []
  })

export const isMastraOverrideModel = (modelId: string): boolean => MASTRA_OVERRIDE_MODELS.some((m) => m.id === modelId)

export enum ECommonFeatureFlag {
  DEFAULT_LLM = "default-llm",
  DOWNTIME_MODE = "downtime-mode",
}

// Analytics events enum that can be used across projects
export enum EUtilAnalyticsEvent {
  THIRD_PARTY_USAGE = "third_party_usage",
  API_ERROR = "API_ERROR",
}

export interface EUtilAnalyticsProperties {
  [EUtilAnalyticsEvent.THIRD_PARTY_USAGE]: {
    source: "elevenlabs" | "openai" | "anthropic" | "replicate"
    credits: number
    operation?: string
    model_id?: string
    content_length?: number
  }
  [EUtilAnalyticsEvent.API_ERROR]: {
    error_code?: string
    context?: string
    message?: string
    endpoint?: string
    http_method?: string
    user_id?: string
    request_id?: string
    stack_trace?: string
  }
}
